import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { AlertTriangle, Download, FileText, Copy, Check, ArrowLeft, Building2, Package, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LowStock = () => {
  const { user } = useAuth();
  const [lowStockItems, setLowStockItems] = useState([]);
  const [purchaseList, setPurchaseList] = useState(null);
  const [emailText, setEmailText] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('low-stock');
  const [copied, setCopied] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [lowStockRes, purchaseRes, emailRes] = await Promise.all([
        axios.get(`${API}/inventory/low-stock`),
        axios.get(`${API}/inventory/purchase-list`),
        axios.get(`${API}/inventory/purchase-list/email-text`)
      ]);
      setLowStockItems(lowStockRes.data);
      setPurchaseList(purchaseRes.data);
      setEmailText(emailRes.data.text);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load low stock data');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyText = () => {
    navigator.clipboard.writeText(emailText);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadCSV = () => {
    if (!purchaseList) return;
    
    let csv = 'Vendor,Item Name,SKU,Category,Unit,On Hand,MOQ,Suggested Qty,Unit Cost,Extended Cost\n';
    
    purchaseList.by_vendor.forEach(group => {
      group.items.forEach(item => {
        csv += `"${group.vendor.name}","${item.name}","${item.sku}","${item.category}","${item.unit_of_measure}",${item.current_quantity},${item.moq},${item.suggested_reorder_qty},${item.last_cost_price},${item.extended_cost}\n`;
      });
    });
    
    purchaseList.no_vendor.forEach(item => {
      csv += `"No Vendor","${item.name}","${item.sku}","${item.category}","${item.unit_of_measure}",${item.current_quantity},${item.moq},${item.suggested_reorder_qty},${item.last_cost_price},${item.extended_cost}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchase-list-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const handlePrintPDF = () => {
    setGeneratingPdf(true);
    setTimeout(() => {
      window.print();
      setGeneratingPdf(false);
    }, 500);
  };

  if (user?.role !== 'owner') {
    return (
      <Layout>
        <div className="p-6 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
          <h2 className="text-xl font-semibold text-slate-800">Access Restricted</h2>
          <p className="text-slate-600">Only Owner Admin can access low stock monitoring.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 print:p-0" data-testid="low-stock-page">
        {/* Header */}
        <div className="page-header mb-6 print:hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/inventory" className="p-2 hover:bg-slate-100 rounded-lg">
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </a>
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-amber-500" />
                <div>
                  <h1 className="page-title">Low Stock & Purchase List</h1>
                  <p className="text-slate-500">{lowStockItems.length} items below reorder level</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleDownloadCSV}>
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
              <Button variant="outline" onClick={handlePrintPDF} disabled={generatingPdf}>
                {generatingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Print PDF
              </Button>
              <Button onClick={handleCopyText}>
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                Copy Email Text
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 print:hidden">
          <button
            onClick={() => setActiveTab('low-stock')}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'low-stock' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            Low Stock Items
          </button>
          <button
            onClick={() => setActiveTab('purchase-list')}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'purchase-list' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            Purchase List (by Vendor)
          </button>
          <button
            onClick={() => setActiveTab('email-text')}
            className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'email-text' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            Email-Ready Text
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading...</div>
        ) : (
          <>
            {/* Low Stock Items Tab */}
            {activeTab === 'low-stock' && (
              <div className="card overflow-hidden">
                {lowStockItems.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-4 text-green-500" />
                    <p className="font-medium text-green-700">All items are above reorder level!</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-amber-50 border-b border-amber-200">
                      <tr>
                        <th className="text-left p-3 font-medium text-amber-800">Item</th>
                        <th className="text-left p-3 font-medium text-amber-800">SKU</th>
                        <th className="text-center p-3 font-medium text-amber-800">On Hand</th>
                        <th className="text-center p-3 font-medium text-amber-800">MOQ</th>
                        <th className="text-center p-3 font-medium text-amber-800">Suggested Order</th>
                        <th className="text-right p-3 font-medium text-amber-800">Unit Cost</th>
                        <th className="text-right p-3 font-medium text-amber-800">Extended</th>
                        <th className="text-left p-3 font-medium text-amber-800">Vendor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lowStockItems.map((item) => (
                        <tr key={item.id} className="border-b hover:bg-amber-50">
                          <td className="p-3 font-medium text-slate-800">{item.name}</td>
                          <td className="p-3 text-slate-600 font-mono text-sm">{item.sku}</td>
                          <td className="p-3 text-center">
                            <span className={`font-semibold ${item.current_quantity <= 0 ? 'text-red-600' : 'text-amber-600'}`}>
                              {item.current_quantity}
                            </span>
                            <span className="text-slate-500 text-sm ml-1">{item.unit_of_measure}</span>
                          </td>
                          <td className="p-3 text-center text-slate-600">{item.moq}</td>
                          <td className="p-3 text-center font-semibold text-blue-600">{item.suggested_reorder_qty}</td>
                          <td className="p-3 text-right text-slate-600">${item.last_cost_price?.toFixed(2)}</td>
                          <td className="p-3 text-right font-semibold text-slate-800">${item.extended_cost?.toFixed(2)}</td>
                          <td className="p-3 text-slate-600">{item.vendor?.name || <span className="text-slate-400 italic">No vendor</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t-2">
                      <tr>
                        <td colSpan="6" className="p-3 text-right font-semibold text-slate-700">Total Estimated Cost:</td>
                        <td className="p-3 text-right font-bold text-slate-900">
                          ${lowStockItems.reduce((sum, item) => sum + (item.extended_cost || 0), 0).toFixed(2)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            )}

            {/* Purchase List by Vendor Tab */}
            {activeTab === 'purchase-list' && purchaseList && (
              <div className="space-y-6">
                {purchaseList.by_vendor.length === 0 && purchaseList.no_vendor.length === 0 ? (
                  <div className="card p-8 text-center text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-4 text-green-500" />
                    <p className="font-medium text-green-700">No items need reordering!</p>
                  </div>
                ) : (
                  <>
                    {purchaseList.by_vendor.map((group, idx) => (
                      <div key={idx} className="card overflow-hidden">
                        <div className="p-4 bg-blue-50 border-b border-blue-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Building2 className="w-6 h-6 text-blue-600" />
                              <div>
                                <h3 className="font-semibold text-blue-900">{group.vendor.name}</h3>
                                <p className="text-sm text-blue-700">
                                  {group.vendor.contact_person && `${group.vendor.contact_person} • `}
                                  {group.vendor.phone && `${group.vendor.phone} • `}
                                  {group.vendor.email}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-blue-700">{group.items.length} items</p>
                              <p className="font-bold text-blue-900">${group.total_cost.toFixed(2)}</p>
                            </div>
                          </div>
                        </div>
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b">
                            <tr>
                              <th className="text-left p-3 font-medium text-slate-600">Item</th>
                              <th className="text-left p-3 font-medium text-slate-600">SKU</th>
                              <th className="text-center p-3 font-medium text-slate-600">On Hand</th>
                              <th className="text-center p-3 font-medium text-slate-600">Order Qty</th>
                              <th className="text-right p-3 font-medium text-slate-600">Unit Cost</th>
                              <th className="text-right p-3 font-medium text-slate-600">Extended</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.items.map((item) => (
                              <tr key={item.id} className="border-b hover:bg-slate-50">
                                <td className="p-3 font-medium text-slate-800">{item.name}</td>
                                <td className="p-3 text-slate-600 font-mono text-sm">{item.sku}</td>
                                <td className="p-3 text-center text-slate-600">{item.current_quantity} {item.unit_of_measure}</td>
                                <td className="p-3 text-center font-semibold text-blue-600">{item.suggested_reorder_qty}</td>
                                <td className="p-3 text-right text-slate-600">${item.last_cost_price.toFixed(2)}</td>
                                <td className="p-3 text-right font-semibold text-slate-800">${item.extended_cost.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}

                    {purchaseList.no_vendor.length > 0 && (
                      <div className="card overflow-hidden">
                        <div className="p-4 bg-slate-100 border-b border-slate-200">
                          <div className="flex items-center gap-3">
                            <AlertTriangle className="w-6 h-6 text-slate-500" />
                            <div>
                              <h3 className="font-semibold text-slate-700">Items Without Vendor</h3>
                              <p className="text-sm text-slate-500">Assign vendors to these items for organized ordering</p>
                            </div>
                          </div>
                        </div>
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b">
                            <tr>
                              <th className="text-left p-3 font-medium text-slate-600">Item</th>
                              <th className="text-left p-3 font-medium text-slate-600">SKU</th>
                              <th className="text-center p-3 font-medium text-slate-600">On Hand</th>
                              <th className="text-center p-3 font-medium text-slate-600">Order Qty</th>
                              <th className="text-right p-3 font-medium text-slate-600">Unit Cost</th>
                              <th className="text-right p-3 font-medium text-slate-600">Extended</th>
                            </tr>
                          </thead>
                          <tbody>
                            {purchaseList.no_vendor.map((item) => (
                              <tr key={item.id} className="border-b hover:bg-slate-50">
                                <td className="p-3 font-medium text-slate-800">{item.name}</td>
                                <td className="p-3 text-slate-600 font-mono text-sm">{item.sku}</td>
                                <td className="p-3 text-center text-slate-600">{item.current_quantity} {item.unit_of_measure}</td>
                                <td className="p-3 text-center font-semibold text-blue-600">{item.suggested_reorder_qty}</td>
                                <td className="p-3 text-right text-slate-600">${item.last_cost_price.toFixed(2)}</td>
                                <td className="p-3 text-right font-semibold text-slate-800">${item.extended_cost.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Email Text Tab */}
            {activeTab === 'email-text' && (
              <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800">Email-Ready Purchase Order Text</h3>
                  <Button onClick={handleCopyText} size="sm">
                    {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    Copy to Clipboard
                  </Button>
                </div>
                <pre className="bg-slate-50 p-4 rounded-lg text-sm font-mono text-slate-700 overflow-x-auto whitespace-pre-wrap">
                  {emailText || 'No items to order.'}
                </pre>
              </div>
            )}

            {/* Print View */}
            <div className="hidden print:block">
              <h1 className="text-2xl font-bold mb-4">Purchase Order - {new Date().toLocaleDateString()}</h1>
              {purchaseList?.by_vendor.map((group, idx) => (
                <div key={idx} className="mb-8">
                  <h2 className="text-lg font-semibold border-b pb-2 mb-2">{group.vendor.name}</h2>
                  <p className="text-sm text-slate-600 mb-2">
                    {group.vendor.contact_person} | {group.vendor.phone} | {group.vendor.email}
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1">Item</th>
                        <th className="text-left py-1">SKU</th>
                        <th className="text-center py-1">Qty</th>
                        <th className="text-right py-1">Cost</th>
                        <th className="text-right py-1">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((item) => (
                        <tr key={item.id} className="border-b">
                          <td className="py-1">{item.name}</td>
                          <td className="py-1">{item.sku}</td>
                          <td className="text-center py-1">{item.suggested_reorder_qty} {item.unit_of_measure}</td>
                          <td className="text-right py-1">${item.last_cost_price.toFixed(2)}</td>
                          <td className="text-right py-1">${item.extended_cost.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="font-bold">
                        <td colSpan="4" className="text-right py-2">Subtotal:</td>
                        <td className="text-right py-2">${group.total_cost.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default LowStock;
